import { effect, Injectable } from '@angular/core';
import { Firestore, collection, query, where, orderBy, limit, doc, setDoc, addDoc, getDocs, serverTimestamp, updateDoc } from 'firebase/firestore';
import { collectionData } from 'rxfire/firestore';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Food, FoodOrder } from 'src/interfaces/food.model';
import { Drink, DrinkOrder } from 'src/interfaces/drink.model';
import { Order } from 'src/interfaces/order.model';
import { DataService } from './data.service';
import { Modificator } from 'src/interfaces/modificators.model';
import { cleanForFirestore } from '../utils/firestore-clean';
import { TariffPlan } from 'src/interfaces/enums.model';
import { Table } from 'src/interfaces/table.model';

@Injectable({
    providedIn: 'root'
})
export class OrderService {
    private orderSubject: BehaviorSubject<Order | null> = new BehaviorSubject<Order | null>(null);
    public order$: Observable<Order | null> = this.orderSubject.asObservable();
    private table: Table | null = null;
    private tableId: string | null = null;

    constructor(
        private firestore: Firestore,
        private dataService: DataService
    ) {
        effect(() => {
            this.tableId = this.dataService.getTableId();
            console.log('OrderService | effect | tableId changed to: ', this.tableId);
        });
        effect(() => {
            this.table = this.dataService.getTable();
            console.log('OrderService | effect | Table changed to: ', this.table?.id);
        });
    }

    // Подписка на последний актуальный заказ
    subscribeToOrder(): Observable<Order | null> {
        const restaurantId = this.dataService.restaurant?.id;

        if (!restaurantId) {
            console.error('OrderService | createOrUpdateOrder() | restaurantId is missing', restaurantId);
            this.orderSubject.next(null);
            return of(null);
        }

        if (!this.tableId) {
            console.error('OrderService | createOrUpdateOrder() | tableId is missing', this.tableId);
            this.orderSubject.next(null);
            return of(null);
        }

        console.log('OrderService | subscribeToOrder() | Subscribing with:', {
            restaurantId: restaurantId,
            tableId: this.tableId,
            statuses: ['Created', 'Updated', 'Accepted', 'Process', 'Payment', 'Paid', 'Completed', 'Cancelled', 'Deleted']
        });

        const ordersCollectionRef = collection(this.firestore, 'orders');
        const filteredOrdersQuery = query(
            ordersCollectionRef,
            where('restaurantId', '==', restaurantId),
            where('tableId', '==', this.tableId),
            where('status', 'in', ['Created', 'Updated', 'Accepted', 'Process', 'Payment']),
            orderBy('createdAt', 'desc'),
            limit(1)
        );

        getDocs(filteredOrdersQuery).then(console.log).catch(console.error);

        return collectionData(filteredOrdersQuery, { idField: 'id' }).pipe(
            map(orders => {
                const order = orders[0] as Order;
                console.log('OrderService | subscribeToOrder() | All matching orders:', orders.length, 'id:', order?.id);

                if (order && ['Deleted', 'Cancelled', 'Completed', 'Paid'].includes(order.status)) {
                    console.log(`OrderService | subscribeToOrder() | Order is ${order.status}:`, order);
                    this.orderSubject.next(null);
                    return null;
                }

                if (order) {
                    // Добавляем поле status в FoodOrder и DrinkOrder, если оно отсутствует
                    Object.keys(order.guests).forEach(guestId => {
                        const guest = order.guests[guestId];
                        guest.foods = guest.foods.map((food: FoodOrder) => ({
                            ...food,
                            guestId: food.guestId || guestId,
                            status: food.status || 'Accepted' // Устанавливаем 'Accepted', если status отсутствует
                        }));
                        guest.drinks = guest.drinks.map((drink: DrinkOrder) => ({
                            ...drink,
                            guestId: drink.guestId || guestId,
                            status: drink.status || 'Accepted' // Устанавливаем 'Accepted', если status отсутствует
                        }));
                    });
                }

                console.log('OrderService | subscribeToOrder() | Received order:', order || 'No order found');
                this.orderSubject.next(order || null);
                console.log('OrderService | subscribeToOrder() | orderSubject updated:', order || null);
                return order || null;
            })
        );
    }

    /** Есть свои позиции, которые ещё не приняли (их можно отменить) */
    hasOwnNewItems(order: Order | null): boolean {
        const guest = order?.guests?.[this.dataService.guestId];
        return !!guest && [...(guest.foods ?? []), ...(guest.drinks ?? [])]
            .some((item) => item.status === 'Created');
    }

    /**
     * Отмена гостем — только своих новых (непринятых) позиций. Заказ ещё не
     * приняли и в нём ничего не осталось — заказ «Отменён» (по ошибке или
     * чужой — по сохранённой ссылке). Принятый — убираются только новые
     * позиции, «В работе» вернёт функция onOrderUpdated
     */
    async cancelOwnNewItems(): Promise<void> {
        const order = this.orderSubject.getValue();
        const guestId = this.dataService.guestId;
        const guest = order?.guests?.[guestId];
        if (!order?.id || !guest) return;
        const kept = {
            ...guest,
            foods: (guest.foods ?? []).filter((item) => item.status !== 'Created'),
            drinks: (guest.drinks ?? []).filter((item) => item.status !== 'Created'),
        };
        const left = Object.entries(order.guests)
            .flatMap(([id, g]) => id === guestId ? [...kept.foods, ...kept.drinks] : [...(g.foods ?? []), ...(g.drinks ?? [])])
            .some((item) => item.status !== 'Cancelled');
        await updateDoc(doc(this.firestore, 'orders', order.id), {
            [`guests.${guestId}`]: kept,
            ...(order.status === 'Created' && !left ? { status: 'Cancelled' } : {}),
            updatedAt: serverTimestamp(),
        });
        console.log('OrderService | cancelOwnNewItems() |', order.id, order.status === 'Created' && !left ? 'cancelled' : 'items removed');
    }

    // Создание или обновление заказа
    async createOrUpdateOrder(foods: Food[], drinks: Drink[]): Promise<void> {
        const restaurantId = this.dataService.restaurant?.id;

        const guestId = this.dataService.guestId;
        const groupId = this.dataService.restaurant?.groupId || null;
        const timezone = this.dataService.restaurant?.timezone || "UTC"; // По умолчанию UTC, если не указан часовой пояс

        if (!restaurantId) {
            console.error('OrderService | createOrUpdateOrder() | restaurantId is missing', restaurantId);
            return;
        }

        if (!this.tableId) {
            console.error('OrderService | createOrUpdateOrder() | tableId is missing', this.tableId);
            return;
        }

        // Проверяем текущий заказ
        const currentOrder = this.orderSubject.getValue();

        // Время — сервера (serverTimestamp), а не телефона гостя: раньше
        // строка «часы в поясе заведения» читалась как время в поясе телефона,
        // и при другом поясе или неверных часах время заказа сдвигалось.
        // Показывать — в поясе заведения (поле timezone)
        const orderData: Order = {
            restaurantId: restaurantId,
            tableId: this.tableId,
            description: this.table ? `Стол № ${this.table.index} ${this.table.description}` : '',
            groupId: groupId,
            // createdAt пишется только при создании: при обновлении merge
            // сохраняет серверное, а в локальном снимке до ответа сервера оно
            // ещё null — записать его обратно правила не дали бы
            ...(currentOrder ? {} : { createdAt: serverTimestamp() }),
            updatedAt: serverTimestamp(),
            timezone: currentOrder ? currentOrder.timezone : timezone,
            status: currentOrder ? currentOrder.status === 'Created' ? 'Created' : 'Updated' : 'Created',
            guests: currentOrder?.guests.hasOwnProperty(guestId) ? currentOrder.guests : { [guestId]: { foods: [], drinks: [] } },
            // Официантов стола ставит функция onOrderCreated: гость их не
            // присылает (docs/UUID_MIGRATION.md в edim_app)
        };

        // Номер ставит функция onOrderCreated (orderCounters, транзакция):
        // раньше гостевое меню брало последний номер у api.edim.online, и два
        // одновременных заказа могли получить один номер. При обновлении номер
        // не пишется — merge сохраняет поставленный функцией

        if (this.dataService.restaurant && typeof this.dataService.restaurant === 'object') {
            orderData.tariff = this.dataService.restaurant.tariff || TariffPlan.Free;
            orderData.demo = this.dataService.restaurant.demo;
        }

        if (this.dataService.locale && typeof this.dataService.locale === 'object') {
            orderData.language = this.dataService.locale.code;
            orderData.decimal = this.dataService.locale.decimal;
        }

        // console.log('OrderService | createOrUpdateOrder() | Preparing order data:', JSON.stringify(orderData, null, 2));

        // Подготовка новых блюд и напитков
        const newFoods: FoodOrder[] = foods
            .filter((f: Food) => f.count > 0)
            .map((f: Food) => {
                const rawFood: FoodOrder = {
                    id: f.id,
                    title: `${f.title}${f.variability ? ' <i>' + f.variants[f.choise].title + ' </i>' : ''}`,
                    count: f.count,
                    price: f.totalPrice,
                    choise: f.variability ? f.choise : null,
                    modificators: (typeof f.modificators === 'object')
                        ? f.modificators.items
                            .filter((m: Modificator) => m.count > 0)
                            .map((m: Modificator) => ({
                                id: m.id,
                                count: m.count,
                                title: m.title,
                            }))
                        : [],
                    status: 'Created',
                    guestId: guestId,
                };

                return cleanForFirestore(rawFood);
            });

        const newDrinks: DrinkOrder[] = drinks
            .filter((d: Drink) => d.count > 0)
            .map((d: Drink) => {
                const rawDrink: DrinkOrder = {
                    id: d.id,
                    title: `${d.title}${d.variability ? ' <i>' + d.variants[d.choise].title + ' </i>' : ''}`,
                    count: d.count,
                    price: d.totalPrice,
                    choise: d.variability ? d.choise : null,
                    modificators: (typeof d.modificators === 'object')
                        ? d.modificators.items
                            .filter((m: Modificator) => m.count > 0)
                            .map((m: Modificator) => ({
                                id: m.id,
                                count: m.count,
                                title: m.title,
                            }))
                        : [],
                    status: 'Created',
                    guestId: guestId,
                };

                return cleanForFirestore(rawDrink);
            });

        // Current order data foods and drinks
        const existingFoods = orderData.guests[guestId]?.foods || [];
        const existingDrinks = orderData.guests[guestId]?.drinks || [];

        // Добавляем новые блюда и напитки
        orderData.guests[guestId].foods = [...existingFoods, ...newFoods];
        orderData.guests[guestId].drinks = [...existingDrinks, ...newDrinks];

        // console.log('OrderService | createOrUpdateOrder() | Preparing order data:', JSON.stringify(orderData, null, 2));

        try {
            if (currentOrder?.id) {
                // Обновляем существующий заказ
                const orderDocRef = doc(this.firestore, 'orders', currentOrder.id);
                await setDoc(orderDocRef, orderData, { merge: true });
                console.log('OrderService | createOrUpdateOrder() | Order updated:', {
                    orderId: currentOrder.id,
                    orderData
                });
            } else {
                // Создаем новый заказ
                const ordersCollectionRef = collection(this.firestore, 'orders');
                const docRef = await addDoc(ordersCollectionRef, orderData);
                console.log('OrderService | createOrUpdateOrder() | Order created:', {
                    orderId: docRef.id,
                    orderData
                });
            }
        } catch (error) {
            console.error('OrderService | createOrUpdateOrder() | Failed to create or update order:', {
                error,
                orderData
            });
            this.orderSubject.next(null); // Отменяем подписку при ошибке
            throw error;
        }
    }

    public currentOrder(): Order | null {
        return this.orderSubject.getValue();
    }

    public updateOrder(order: Order): void {
        this.orderSubject.next(order);
    }

}
